import React from 'react';
import { RouteConfigComponentProps } from 'react-router-config';
import { Box } from '@cp/ds/src/components/box';
import { Container } from '@cp/ds/src/components/container';
import { Breadcrumbs } from '@cp/ds/src/components/breadcrumbs';
import { CITY_PATH_PARAMETER, useClientWebResolver } from '@cp/entities/clientWebRoutes';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { CareerId } from '@common/const/catalog/career';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { RoutedLink } from 'src/shared/ui/link';
import Header from 'src/widgets/header';
import { defaultCityRenderer } from 'src/app/shared/components/mapWithLinksRegions/cityRenderer';
import { MapWithLinksRegionsCityRenderer } from 'src/app/shared/components/mapWithLinksRegions';
import { useNavigateToCatalog } from 'src/features/catalog';
import { usePrefilter } from 'src/features/clientSelection';
import { PageMetaTags } from '../../shared/components/layout/components/meta';
import { usePageMetaTags } from '../../shared/hooks/useMetaTags';
import { GeographyOfPresence } from '../../shared/components/geographyOfPresence';

const CitiesPage: React.FC<RouteConfigComponentProps> = () => {
  const renderConfig = usePageMetaTags();
  const resolver = useClientWebResolver();
  const { navigateToCatalog } = useNavigateToCatalog();
  const prefilter = usePrefilter();

  React.useEffect(() => {
    const timeoutId = setTimeout(async () => {
      const answers = await prefilter({ requiredQuestions: ['cityId'] });
      answers.mapAsync((selection) => navigateToCatalog(selection));
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [navigateToCatalog, prefilter]);

  const renderCity = React.useCallback<MapWithLinksRegionsCityRenderer>(
    (city) => {
      const slicedRoutePath = city.routeName.slice(1);
      const cityAlias = slicedRoutePath.slice(0, slicedRoutePath.indexOf('/')) || undefined;

      return (
        <>
          {defaultCityRenderer({
            cityName: `Няни ${city.cityName}`,
            routeName: resolver
              .resolve({
                page: ClientWebPage.Catalog,
                pageProps: { catalogType: CatalogItemType.Resume, careerId: CareerId.Nanny },
                pathParams: { [CITY_PATH_PARAMETER]: cityAlias },
              })
              .toString(),
          })}

          {/* YOU-5106: restore when other specialties are enabled
          {defaultCityRenderer({
            cityName: `Сиделки ${city.cityName}`,
            routeName: resolver
              .resolve({
                page: ClientWebPage.Catalog,
                pageProps: { catalogType: CatalogItemType.Resume, careerId: CareerId.Nurse },
                pathParams: { [CITY_PATH_PARAMETER]: cityAlias },
              })
              .toString(),
          })}

          {defaultCityRenderer({
            cityName: `Репетиторы ${city.cityName}`,
            routeName: resolver
              .resolve({
                page: ClientWebPage.Catalog,
                pageProps: { catalogType: CatalogItemType.Resume, careerId: CareerId.Tutor },
                pathParams: { [CITY_PATH_PARAMETER]: cityAlias },
              })
              .toString(),
          })}

          {defaultCityRenderer({
            cityName: `Домработницы ${city.cityName}`,
            routeName: resolver
              .resolve({
                page: ClientWebPage.Catalog,
                pageProps: { catalogType: CatalogItemType.Resume, careerId: CareerId.Housekeeper },
                pathParams: { [CITY_PATH_PARAMETER]: cityAlias },
              })
              .toString(),
          })}
          */}

          {defaultCityRenderer({
            cityName: `Вакансии Няни ${city.cityName}`,
            routeName: resolver
              .resolve({
                page: ClientWebPage.Catalog,
                pageProps: { catalogType: CatalogItemType.Vacancy, careerId: CareerId.Nanny },
                pathParams: { [CITY_PATH_PARAMETER]: cityAlias },
              })
              .toString(),
          })}

          {/* YOU-5106: restore when other specialties are enabled
          {defaultCityRenderer({
            cityName: `Вакансии Сиделки ${city.cityName}`,
            routeName: resolver
              .resolve({
                page: ClientWebPage.Catalog,
                pageProps: { catalogType: CatalogItemType.Vacancy, careerId: CareerId.Nurse },
                pathParams: { [CITY_PATH_PARAMETER]: cityAlias },
              })
              .toString(),
          })}

          {defaultCityRenderer({
            cityName: `Вакансии Репетиторы ${city.cityName}`,
            routeName: resolver
              .resolve({
                page: ClientWebPage.Catalog,
                pageProps: { catalogType: CatalogItemType.Vacancy, careerId: CareerId.Tutor },
                pathParams: { [CITY_PATH_PARAMETER]: cityAlias },
              })
              .toString(),
          })}

          {defaultCityRenderer({
            cityName: `Вакансии Домработницы ${city.cityName}`,
            routeName: resolver
              .resolve({
                page: ClientWebPage.Catalog,
                pageProps: { catalogType: CatalogItemType.Vacancy, careerId: CareerId.Housekeeper },
                pathParams: { [CITY_PATH_PARAMETER]: cityAlias },
              })
              .toString(),
          })}
          */}
        </>
      );
    },
    [resolver],
  );

  return (
    <Box sx={{ pb: 8 }}>
      <PageMetaTags renderConfig={renderConfig} />

      <Header />

      <Container>
        <Breadcrumbs
          LinkComponent={RoutedLink}
          items={[
            {
              path: '/',
              label: 'Главная',
            },
            {
              path: '',
              label: 'Все города',
            },
          ]}
          sx={{ my: 2 }}
        />
      </Container>

      <GeographyOfPresence disableBackgroundEllipses renderCity={renderCity} />
    </Box>
  );
};

export default CitiesPage;
